import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { TotalReachLogo } from "../components/Logo";
import { Loader2, ArrowRight, CheckCircle2, Sparkles, ArrowLeft, Github } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Marketing-friendly labels — turn "Phase N — Title (2026-08, this fork)" into a
// nice card.
function formatDate(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(y, (m || 1) - 1, d || 1);
    return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  } catch { return iso; }
}

// Minimal markdown → JSX conversion for phase highlights: **bold** + inline
// code. Everything else renders as plain text. Safe because content is
// author-controlled (our own PRD.md).
function renderInline(text) {
  const parts = [];
  let cursor = 0;
  const re = /\*\*(.+?)\*\*|`([^`]+)`/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > cursor) parts.push(text.slice(cursor, m.index));
    if (m[1]) parts.push(<b key={m.index} className="text-white">{m[1]}</b>);
    else if (m[2]) parts.push(<code key={m.index} className="text-cyan-300 bg-cyan-400/10 rounded px-1 py-0.5 text-[10px]">{m[2]}</code>);
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

export default function WhatsNew() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public/whats-new?limit=20`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || e?.message || "Could not load changelog"));
  }, []);

  const [featured, ...rest] = data?.phases || [];

  const monthGroups = useMemo(() => {
    if (!rest?.length) return [];
    const by = new Map();
    for (const p of rest) {
      const key = p.date || "Earlier";
      const label = key === "Earlier" ? "Earlier releases" : formatDate(key);
      if (!by.has(label)) by.set(label, []);
      by.get(label).push(p);
    }
    return Array.from(by.entries());
  }, [rest]);

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white">
      {/* Nav */}
      <nav className="border-b border-white/10 sticky top-0 z-30 bg-[#0A0E1A]/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="whats-new-back" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back to totalreach.us
          </button>
          <TotalReachLogo size={22} />
          <button onClick={() => navigate("/login")}
            data-testid="whats-new-signin"
            className="text-sm px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold">
            Sign in
          </button>
        </div>
      </nav>

      {/* Header */}
      <header className="max-w-6xl mx-auto px-6 pt-14 pb-6" data-testid="whats-new-header">
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-300 font-medium mb-3">Changelog</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">What&apos;s New in TotalReach</h1>
        <p className="text-slate-400 mt-4 max-w-2xl text-base leading-relaxed">
          Real shipping momentum, in the open. Every phase below is a numbered set of features that went live on totalreach.us — tested, deployed, and available to every customer immediately.
        </p>
        {data && (
          <div className="mt-6 flex items-center gap-3 text-xs text-slate-500 flex-wrap" data-testid="whats-new-summary-strip">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/25 text-cyan-200">
              <Sparkles className="w-3 h-3" /> {data.total_phases || 0} phases shipped
            </span>
            <span>Source of truth: <code className="text-cyan-300">{data.source || "PRD.md"}</code></span>
          </div>
        )}
      </header>

      {err && (
        <div className="max-w-6xl mx-auto px-6 py-12" data-testid="whats-new-error">
          <div className="rounded-xl border border-rose-400/30 bg-rose-400/5 p-6 text-sm text-rose-200">{err}</div>
        </div>
      )}

      {!data && !err && (
        <div className="max-w-6xl mx-auto px-6 py-24 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />
        </div>
      )}

      {/* Featured latest phase */}
      {featured && (
        <section className="max-w-6xl mx-auto px-6 pb-8" data-testid="whats-new-featured">
          <article className="rounded-3xl border border-cyan-400/30 bg-gradient-to-br from-cyan-400/[0.08] via-blue-500/[0.03] to-transparent p-8 lg:p-10">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="text-[10px] uppercase tracking-[0.25em] text-cyan-300 font-semibold">Latest</span>
              <span className="text-[10px] text-slate-500">Phase {featured.number}</span>
              {featured.date && <span className="text-[10px] text-slate-500">· {formatDate(featured.date)}</span>}
              {featured.tested_iteration && (
                <span className="text-[10px] inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 font-semibold">
                  <CheckCircle2 className="w-2.5 h-2.5" /> Tested iteration_{featured.tested_iteration}
                </span>
              )}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight" data-testid="whats-new-featured-title">
              {featured.title}
            </h2>
            {featured.summary && (
              <p className="text-slate-300 mt-4 text-base leading-relaxed max-w-3xl" data-testid="whats-new-featured-summary">
                {renderInline(featured.summary)}
              </p>
            )}
            {(featured.highlights || []).length > 0 && (
              <ul className="mt-6 space-y-3 max-w-3xl">
                {featured.highlights.slice(0, 6).map((h, i) => (
                  <li key={i} className="flex gap-3 text-sm text-slate-300" data-testid={`whats-new-featured-highlight-${i}`}>
                    <CheckCircle2 className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{renderInline(h)}</span>
                  </li>
                ))}
              </ul>
            )}
          </article>
        </section>
      )}

      {/* Timeline */}
      {monthGroups.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="whats-new-timeline">
          {monthGroups.map(([label, phases]) => (
            <div key={label} className="mt-10">
              <div className="text-xs uppercase tracking-[0.3em] text-slate-500 font-medium mb-4 sticky top-16 bg-[#0A0E1A]/90 backdrop-blur py-2 -mx-6 px-6 z-10">
                {label}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {phases.map(p => (
                  <article key={p.number} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-cyan-400/30 transition-colors"
                           data-testid={`whats-new-card-${p.number}`}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-cyan-300">Phase {p.number}</span>
                      {p.date && <span className="text-[10px] text-slate-500">· {formatDate(p.date)}</span>}
                      {p.tested_iteration && (
                        <span className="text-[10px] inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 bg-emerald-400/10 border border-emerald-400/25 text-emerald-300">
                          <CheckCircle2 className="w-2.5 h-2.5" /> Tested
                        </span>
                      )}
                    </div>
                    <h3 className="font-display text-lg font-bold text-white leading-tight" data-testid={`whats-new-title-${p.number}`}>
                      {p.title}
                    </h3>
                    {p.summary && (
                      <p className="text-sm text-slate-400 mt-2 leading-relaxed" data-testid={`whats-new-summary-${p.number}`}>
                        {renderInline(p.summary.length > 260 ? p.summary.slice(0, 260).trim() + "…" : p.summary)}
                      </p>
                    )}
                    {(p.highlights || []).length > 0 && (
                      <ul className="mt-3 space-y-1.5">
                        {p.highlights.slice(0, 3).map((h, i) => (
                          <li key={i} className="flex gap-2 text-xs text-slate-400 leading-relaxed">
                            <span className="text-cyan-300 select-none">›</span>
                            <span>{renderInline(h.length > 140 ? h.slice(0, 140).trim() + "…" : h)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-white/10 mt-12">
        <div className="max-w-6xl mx-auto px-6 py-16 text-center" data-testid="whats-new-cta">
          <div className="text-xs uppercase tracking-[0.3em] text-cyan-300 font-medium mb-2">See it live</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Try every one of these features on your race.</h2>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <button onClick={() => navigate("/login")}
              data-testid="whats-new-primary-cta"
              className="rounded-full px-6 py-3 bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold inline-flex items-center gap-1.5">
              Start free trial <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => navigate("/")}
              data-testid="whats-new-secondary-cta"
              className="rounded-full px-6 py-3 border border-white/15 text-slate-200 hover:border-cyan-400/50 hover:text-cyan-300">
              Back to overview
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-xs text-slate-600 flex items-center justify-center gap-3">
        © TotalReach.us · <a href="/" className="hover:text-cyan-300">Home</a> · <a href="/pricing" className="hover:text-cyan-300">Pricing</a>
      </footer>
    </div>
  );
}
